import { navigate } from "svelte-routing";
import { writable } from "svelte/store";
import getMap from "../utils/getMap";
import request from "../utils/request";
class User {
    constructor() {
        this.fetching = false;
        this.inSync = true;
        this.mode = "dark";
        this._blank = {
            id: null,
            email: "",
            name: "",
            token: null,
            characters: {},
            expiresAt: null,
            fetching: false,
            inSync: true,
            mode: window.matchMedia("(prefers-color-scheme: dark)")
                ? "dark"
                : "light",
        };
        const { subscribe, set } = writable(this._getLs() || {});
        this.subscribe = subscribe;
        this._set = set;
        this._clear = () => {
            this.set(this._blank);
        };
        this._init();
    }
    set(values) {
        for (const k in values) {
            this[k] = values[k];
            this._set(this);
        }
        this._setLs(values);
    }
    _init() {
        const ls = this._getLs();
        if (!ls) {
            this.set(this._blank);
        }
        else {
            this.set(Object.assign(Object.assign({}, ls), { fetching: false }));
        }
    }
    _getLs() {
        const string = localStorage.getItem("user");
        return string ? JSON.parse(string || "{}") : null;
    }
    _setLs(user) {
        const ls = this._getLs() || {};
        for (const k in user) {
            if (["token", "fetching", "notification"].includes(k)) {
                continue;
            }
            ls[k] = user[k];
        }
        localStorage.setItem("user", JSON.stringify(ls));
    }
    async _getRefreshToken(throwError = false) {
        var _a, _b;
        try {
            const data = await request("/user/session/refresh", "POST", null, {
                data: {
                    refresh: (_a = this.refreshToken) === null || _a === void 0 ? void 0 : _a.value,
                },
            });
            this.set({
                token: data.token,
                expiresAt: data.expiresAt,
            });
            return true;
        }
        catch (e) {
            if (((_b = e.response) === null || _b === void 0 ? void 0 : _b.status) === 401) {
                this.notify("Please login to continue.", "error", true);
                this._clear();
                navigate("/login");
            }
            if (throwError) {
                throw e;
            }
            else {
                return false;
            }
        }
    }
    async _Request(target, method, options) {
        var _a;
        this.set({ fetching: true });
        try {
            return await request(target, method, null, options);
        }
        catch (e) {
            if (e.response.status == 429) {
                const reset = e.response.headers["x-ratelimit-reset"];
                const inMinutes = (reset - Math.floor(Date.now() / 1000)) / 60;
                switch (target) {
                    case "/user/login":
                        const id = (_a = options.data) === null || _a === void 0 ? void 0 : _a.identifier;
                        this.notify(`Login attempts exceeded. <a id="notif-link" href="/recover?id=${id}">Click here to reset password</a>.`, "error", false);
                        break;
                    default:
                        this.notify(`You are doing that too many times! Please try again in ${inMinutes} minutes.`, "error");
                }
            }
            throw e;
        }
        finally {
            this.set({ fetching: false });
        }
    }
    async _request(target, method, options) {
        this.set({ fetching: true });
        try {
            if (!this.token || this.expiresAt <= Date.now()) {
                await this._getRefreshToken();
            }
            return await request(target, method, this.token, options);
        }
        catch (e) {
            if (e.response) {
                if (e.response.status === 401 &&
                    ["auth-03", "auth-01"].includes(e.response.data.code)) {
                    const auth = await this._getRefreshToken(false);
                    if (auth) {
                        return this._request(target, method, options);
                    }
                }
                else if (e.response.status == 429) {
                    const reset = e.response.headers["x-ratelimit-reset"];
                    const inMinutes = (reset - Math.floor(Date.now() / 1000)) / 60;
                    this.notify(`You are doing that too many times! Please try again in ${inMinutes} minutes.`, "error");
                }
                else {
                    throw e;
                }
            }
            else {
                throw e;
            }
        }
        finally {
            this.set({ fetching: false });
        }
    }
    _initCharacters(chars) {
        if (!chars.length) {
            // @ts-ignore
            return [
                {
                    id: null,
                    race: null,
                    userId: this.id,
                    armorClass: 0,
                    charisma: 0,
                    constitution: 0,
                    dexterity: 0,
                    experience: 0,
                    hp: 0,
                    initiative: 0,
                    intelligence: 0,
                    level: 0,
                    maxHp: 0,
                    strength: 0,
                    wisdom: 0,
                    modifiedAt: null,
                    createdAt: null,
                    name: "Character name",
                    update: async (options) => {
                        const res = await this._request(`/characters`, "POST", {
                            data: options,
                        });
                        return res;
                    },
                    inventory: {
                        id: null,
                        characterId: null,
                        wallet: {
                            copper: 0,
                            silver: 0,
                            gold: 0,
                            electrum: 0,
                            platinum: 0,
                            update: async (coins) => {
                                const char = await this._request(`/characters`, "POST", {
                                    data: {
                                        name: "Character name",
                                    },
                                });
                                await this.getCharacters(char);
                                await this._request(`/characters/${char.id}/wallet`, "PATCH", {
                                    data: coins,
                                });
                                return coins;
                            },
                        },
                    },
                },
            ];
        }
        const characters = chars.map((char) => (Object.assign(Object.assign({}, char), { update: async (options) => {
                const res = await this._request(`/characters/${char.id}`, "PATCH", {
                    data: options,
                });
                return res;
            }, delete: async () => {
                await this._request(`/characters/${char.id}`, "DELETE");
            }, inventory: Object.assign(Object.assign({}, char.inventory), { addItem: async (item) => {
                    return await this._request(`/characters/${char.id}/inventory`, "POST", {
                        data: item,
                    });
                }, removeItem: async (itemId) => {
                    await this._request(`/characters/${char.id}/inventory/${itemId}`, "DELETE");
                }, updateItem: async (itemId, item) => {
                    return await this._request(`/characters/${char.id}/inventory/${itemId}`, "PATCH", {
                        data: item,
                    });
                }, wallet: Object.assign(Object.assign({}, char.inventory.wallet), { update: async (coins) => {
                        for (const c in coins) {
                            char.inventory.wallet[c] = coins[c];
                        }
                        await this._request(`/characters/${char.id}/wallet`, "PATCH", {
                            data: coins,
                        });
                        await this.getCharacters(char);
                        return char.inventory.wallet;
                    }, getTotalAs: (type) => {
                        const { gold, copper, electrum, platinum, silver } = char.inventory.wallet;
                        const asGold = gold +
                            copper / 100 +
                            electrum / 2 +
                            platinum * 10 +
                            silver / 10;
                        switch (type) {
                            case "copper":
                                return asGold * 100;
                            case "electrum":
                                return asGold * 2;
                            case "platinum":
                                return asGold / 10;
                            case "silver":
                                return asGold * 10;
                            default:
                                return asGold;
                        }
                    } }) }) })));
        return characters;
    }
    notify(message, type, autoDismiss = true) {
        this.set({
            notification: `${type}|${message}`,
        });
        if (autoDismiss) {
            setTimeout(() => {
                console.log("dismissed");
                this.set({
                    notification: null,
                });
            }, 5000);
        }
    }
    async signup(details) {
        const res = await this._Request("/user/signup", "POST", {
            data: details,
        });
        this.set(res);
    }
    async login(identifier, password) {
        const user = await this._Request("/user/login", "POST", {
            data: {
                identifier,
                password,
            },
        });
        this.set(user);
    }
    async logout() {
        this.set({ fetching: true });
        try {
            await request("/user/logout", "POST", this.token);
        }
        catch (e) {
        }
        finally {
            this._clear();
            localStorage.removeItem("user");
        }
    }
    async getProfile() {
        const profile = await this._request("/user/profile");
        this.set(profile);
    }
    async getCharacters(updates) {
        if (updates) {
            if (updates.id in this.characters) {
                for (const k in updates) {
                    this.characters[updates.id][k] = updates[k];
                }
            }
        }
        let chars = Object.values(this.characters || {});
        if (!updates) {
            chars = await this._request("/characters");
        }
        chars &&
            this.set({ characters: getMap(this._initCharacters(chars), "id") });
    }
    async verifyPhone(phone, code) {
        await this._request(`/user/phone/${phone}/verify`, "PATCH", {
            data: {
                code,
            },
        });
        await this._getRefreshToken();
    }
    async verifyEmail(email, code) {
        await this._request(`/user/email/${encodeURIComponent(email)}/verify`, "PATCH", {
            data: {
                code,
            },
        });
        await this._getRefreshToken();
    }
    async discordLogin(code) {
        const res = await this._Request("/user/oauth/discord", "POST", {
            data: {
                code,
            },
        });
        this.set(res);
        return this;
    }
    async recover(identifier) {
        return await this._Request("/user/recover", "POST", {
            data: {
                identifier,
            },
        });
    }
    async resendPhoneCode(phone) {
        return await this._request(`/user/phone/${phone}/send`, "POST");
    }
    async resendEmailCode(email) {
        return await this._request(`/user/email/${encodeURIComponent(email)}`, "POST");
    }
    async addPhone(phone) {
        return await this._request(`/user/phone`, "POST", {
            data: {
                phone,
            },
        });
    }
}
export const user = new User();
export default user;
